import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useSessionStorage } from './useSessionStorage'

const REFRESH_THRESHOLD_SECONDS = 60 // Refresh the token if it's within 60 seconds of expiring

export const isTokenExpired = (providedToken?: string): boolean => {
  const token = providedToken || (JSON.parse(sessionStorage.getItem('token') || '') as string)
  if (!token) return true

  try {
    const [, payloadBase64] = token.split('.')
    const payload = JSON.parse(atob(payloadBase64))
    const now = Math.floor(Date.now() / 1000)

    return payload.exp < now
  } catch {
    toast.info('Decoding token failed. Treating token as expired.')
    return true
  }
}

// ✅ Memoized function to check if the token is near expiry
export const isTokenNearExpiry = (providedToken?: string): boolean => {
  const token = providedToken || (JSON.parse(sessionStorage.getItem('token') || '') as string)
  if (!token) return true

  try {
    const [, payloadBase64] = token.split('.')
    const payload = JSON.parse(atob(payloadBase64))
    const now = Math.floor(Date.now() / 1000)

    return payload.exp - now <= REFRESH_THRESHOLD_SECONDS
  } catch {
    toast.info('Token decoding failed. Attempting to refresh token...')
    return true
  }
}

export const useTokenValidator = () => {
  const [isRefreshing, setIsRefreshing] = useState(false)

  // ✅ Use the custom session storage hook
  const [storedToken, setStoredToken] = useSessionStorage('token', '')

  // ✅ Memoized function to check if the token is expired

  useEffect(() => {
    // ✅ Function to refresh the token
    const refreshAuthToken = async () => {
      if (isRefreshing) return

      setIsRefreshing(true)

      try {
        const response = await fetch('/rest/auth/refreshToken', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          setStoredToken(data.token) // ✅ Use the hook to update the token
          toast.success('Token refreshed successfully.')
        } else {
          throw new Error('Failed to refresh token')
        }
      } catch {
        toast.error('Session expired. Please log in again.')
        sessionStorage.clear() // ✅ Clears all session data
        window.location.href = '/' // ✅ Redirect to home
      } finally {
        setIsRefreshing(false)
      }
    }

    // ✅ Function to validate the token
    const validateToken = () => {
      if (!storedToken || isTokenExpired()) {
        toast.error('Your session has expired. Please log in again.')
        sessionStorage.clear() // ✅ Clears all session data
        window.location.href = '/' // ✅ Redirect to home
      } else if (isTokenNearExpiry()) {
        refreshAuthToken()
      }
    }

    validateToken()
  }, [storedToken, isRefreshing, setStoredToken])

  return { isTokenExpired, isTokenNearExpiry }
}
